import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, ValidatorFn, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA} from '@angular/material/dialog';
import {Device} from 'smag-commons/cfg/connector';
import {isDefined} from '../../../commons/commons';
import * as moment from 'moment-timezone';
import {ConfigurationService} from '../../../shared/configuration.service';
import {takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';

export interface PollingPolicyData {
    devId: string;
    dev: Device;
}

export enum PollingPolicyType {
    NONE,
    INTERVAL,
    CRON
}

export interface PollingPolicy {
    type: PollingPolicyType;
    interval?: number;
    cron?: string;
    timeZone?: string;
}

export function setDevicePolicy<D extends Device>(dev: D, policy: PollingPolicy): D {
    const devPatch = {interval: null, cron: null, timezone: null};
    switch (policy.type) {
        case PollingPolicyType.NONE:
            break;
        case PollingPolicyType.INTERVAL:
            devPatch.interval = policy.interval;
            break;
        case PollingPolicyType.CRON:
            devPatch.cron = policy.cron;
            devPatch.timezone = policy.timeZone;
            break;
    }
    return {...dev, ...devPatch};
}

@Component({
    selector: 'app-polling-policy',
    templateUrl: './polling-policy.component.html',
    styleUrls: ['./polling-policy.component.scss']
})
export class PollingPolicyComponent implements OnInit, OnDestroy {

    private destroyed = new Subject<void>();
    private defaultTimeZone: string;

    readonly timeZones: string[];
    form: FormGroup;

    // exported in HTML
    PollingPolicyType = PollingPolicyType;

    constructor(@Inject(MAT_DIALOG_DATA) readonly data: PollingPolicyData,
                private readonly configurationService: ConfigurationService,
                private fb: FormBuilder) {
        this.defaultTimeZone = null;
        this.timeZones = moment.tz.names();
        this.form = this.fb.group({
            type: new FormControl(getDevPollingPolicyType(data.dev), [Validators.required])
        });
        this.form.addControl('interval',
            new FormControl(data.dev.interval || null, [pollingPolicyIntervalValidator(() => this.form.controls.type.value)]));
        this.form.addControl('cron',
            new FormControl(data.dev.cron || null, [pollingPolicyCronValidator(() => this.form.controls.type.value)]));
        this.form.addControl('timeZone',
            new FormControl(data.dev.timezone || null, pollingPolicyTimezoneValidator(() => this.form.controls.type.value)));
    }

    ngOnInit() {
        this.configurationService.timeConfig.pipe(
            takeUntil(this.destroyed)
        ).subscribe(timeCfg => {
            this.defaultTimeZone = timeCfg.time_zone;
        })
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    onSelectPolicyType() {
        this.form.controls.interval.setValue(null);
        this.form.controls.cron.setValue(null);
        this.form.controls.timeZone.setValue(this.defaultTimeZone);
        this.form.markAsUntouched();
    }

    close(): PollingPolicy {
        switch (this.form.controls.type.value) {
            case PollingPolicyType.NONE:
                return {
                    type: PollingPolicyType.NONE
                };
            case PollingPolicyType.INTERVAL:
                return {
                    type: PollingPolicyType.INTERVAL,
                    interval: this.form.controls.interval.value
                };
            case PollingPolicyType.CRON:
                return {
                    type: PollingPolicyType.CRON,
                    cron: this.form.controls.cron.value ? this.form.controls.cron.value.trim() : null,
                    timeZone: this.form.controls.timeZone.value
                };
        }
    }
}

function pollingPolicyIntervalValidator(typeGetter: () => PollingPolicyType): ValidatorFn {
    return ctrl => {
        if (typeGetter() === PollingPolicyType.INTERVAL) {
            const interval = ctrl.value;
            if (!isDefined(interval) || interval < 0) {
                return {bad_interval: true};
            }
        }
        return null;
    };
}

function pollingPolicyCronValidator(typeGetter: () => PollingPolicyType): ValidatorFn {
    return ctrl => {
        if (typeGetter() === PollingPolicyType.CRON) {
            const cron = ctrl.value;
            if (!cron || !cron.trim().length) {
                return {bad_cron: true};
            }
        }
        return null;
    };
}

function pollingPolicyTimezoneValidator(typeGetter: () => PollingPolicyType): ValidatorFn {
    return ctrl => {
        if (typeGetter() === PollingPolicyType.CRON) {
            const timeZone = ctrl.value;
            if (!timeZone) {
                return {bad_timezone: true};
            }
        }
        return null;
    };
}

function getDevPollingPolicyType(dev: Device): PollingPolicyType {
    if (isDefined(dev.interval)) {
        return PollingPolicyType.INTERVAL;
    } else if (dev.cron) {
        return PollingPolicyType.CRON;
    } else {
        return PollingPolicyType.NONE;
    }
}
